import { Draft } from 'immer';

import { Link } from '../links';
import {
  LinkBuilder,
  LinkBuilderInitial,
  LinkBuilderFinalizable,
} from '../links/definition/linkBuilder';
import { applyState, toSuccess } from '../resolvers/definition/resolverState';
import { produceNext } from '../utils/immer';
import {
  applyDefaultsToEgg,
  LinkResult,
  SELF,
  type SelfLink,
} from './resource';
import { Resource } from './resourceMethods';
import { defaultResourceRegistry, ResourceRegistry } from './resourceRegistry';
import {
  AnyResourceDefinition,
  ArgEntry,
  ResourceDefinition,
  ResourceDefinitionEgg,
  RejectUndefinedData,
  HashConfig,
} from './types';

// ─── Transform types (self = derived, other = source) ───────────────

export type DerivedPullTransform<
  TTargetParams,
  TTargetData,
  TSourceParams,
  TSourceData,
> = (opts: {
  self: { params: TTargetParams };
  other: { params: TSourceParams; data: TSourceData };
}) => TTargetData;

export type DerivedPushTransform<
  TTargetParams,
  TTargetData,
  TSourceParams,
  TSourceData,
> = (opts: {
  self: { params: TTargetParams; data: TTargetData };
  other: { params: TSourceParams; data?: Draft<TSourceData> };
}) => TSourceData | void;

export type DerivedPushImmutableTransform<
  TTargetParams,
  TTargetData,
  TSourceParams,
  TSourceData,
> = (opts: {
  self: { params: TTargetParams; data: TTargetData };
  other: { params: TSourceParams; data?: TSourceData };
}) => TSourceData | void;

// ─── Public step types ─────────────────────────────────────────────

export interface PullStep<
  TTargetParams,
  TSourceParams,
  TSourceData,
  TSourceError extends Error = Error,
> {
  pull: <TTargetData>(
    transform: DerivedPullTransform<
      TTargetParams,
      TTargetData,
      TSourceParams,
      TSourceData
    >
  ) => RejectUndefinedData<
    TTargetData,
    Builder<
      TSourceParams,
      TSourceData,
      TTargetData,
      TTargetParams,
      TSourceError
    >
  >;
  mergeArgs: (
    keys: Array<ArgEntry<TTargetParams>>
  ) => PullStep<TTargetParams, TSourceParams, TSourceData, TSourceError>;
  withArgs: (
    keys: Array<ArgEntry<TTargetParams>>
  ) => PullStep<TTargetParams, TSourceParams, TSourceData, TSourceError>;
}

/**
 * After `.mapParams` the derived resource has its own param shape, so its
 * identity can't be inherited from the source — declare it with `.mergeArgs`
 * or `.withArgs` before `.pull`. (Omit `.mapParams` entirely to keep the
 * source's params and inherit its identity.)
 */
export interface MappedStep<
  TTargetParams,
  TSourceParams,
  TSourceData,
  TSourceError extends Error = Error,
> {
  mergeArgs: (
    keys: Array<ArgEntry<TTargetParams>>
  ) => PullStep<TTargetParams, TSourceParams, TSourceData, TSourceError>;
  withArgs: (
    keys: Array<ArgEntry<TTargetParams>>
  ) => PullStep<TTargetParams, TSourceParams, TSourceData, TSourceError>;
}

// ─── Builder ────────────────────────────────────────────────────────

export interface Builder<
  TSourceParams,
  TSourceData,
  TTargetData,
  TTargetParams,
  TSourceError extends Error = Error,
> extends LinkResult<TTargetParams, TTargetData, TSourceError> {
  push: (
    fn: DerivedPushTransform<
      TTargetParams,
      TTargetData,
      TSourceParams,
      TSourceData
    >
  ) => this;
  pushImmutable: (
    fn: DerivedPushImmutableTransform<
      TTargetParams,
      TTargetData,
      TSourceParams,
      TSourceData
    >
  ) => this;
  withHash: (fn: HashConfig<TTargetData>) => this;
}

const buildDeriveChain = <
  TSourceParams,
  TSourceData,
  TTargetParams,
  TTargetData,
  TSourceError extends Error = Error,
>(
  name: string,
  source: ResourceDefinition<TSourceParams, TSourceData, TSourceError>,
  paramMapper: (params: TTargetParams) => TSourceParams,
  pullTransform: DerivedPullTransform<
    TTargetParams,
    TTargetData,
    TSourceParams,
    TSourceData
  >,
  derivedArgs: Array<ArgEntry<TTargetParams>> | undefined,
  mergeWithSource: boolean
): Builder<
  TSourceParams,
  TSourceData,
  TTargetData,
  TTargetParams,
  TSourceError
> => {
  let hash: HashConfig<TTargetData>;
  let pushTransform: DerivedPushTransform<
    TTargetParams,
    TTargetData,
    TSourceParams,
    TSourceData
  >;
  let pushImmutableTransform: DerivedPushImmutableTransform<
    TTargetParams,
    TTargetData,
    TSourceParams,
    TSourceData
  >;

  const deferredLinks: Array<{
    target: AnyResourceDefinition | SelfLink;
    construct: (
      builder: LinkBuilderInitial<any, any, any, any>
    ) => LinkBuilderFinalizable<any, any, any, any>;
  }> = [];

  const register = (__registry: ResourceRegistry = defaultResourceRegistry) => {
    const inheritedArgs = mergeWithSource
      ? ((source.args ?? []) as unknown as Array<ArgEntry<TTargetParams>>)
      : [];
    const mergedArgs =
      inheritedArgs.length > 0 || derivedArgs
        ? [...inheritedArgs, ...(derivedArgs ?? [])]
        : undefined;

    const resource = applyDefaultsToEgg({
      typeName: name,
      args: mergedArgs,
      hash,
      sources: [{ resource: source, mapParams: paramMapper }],
    } as unknown as ResourceDefinitionEgg<
      TTargetParams,
      TTargetData,
      TSourceError
    >);

    // self = derived (resource), other = source
    const linkBuilder = new LinkBuilder<
      TTargetParams,
      TTargetData,
      TSourceParams,
      TSourceData
    >(resource, source)
      .findOther({
        fromSelfParams: ({ params }: { params: TTargetParams }) =>
          paramMapper(params),
      })
      .pullState(({ dataChanged, self, other }) => {
        if (!dataChanged || other.state?.data === undefined) {
          return applyState(other.state, self.state);
        }
        const nextData = pullTransform({
          self: { params: self.params },
          other: { params: other.params, data: other.state.data },
        });
        return applyState(other.state, toSuccess(nextData)());
      });

    const pushFn = (pushTransform ?? pushImmutableTransform) as
      | DerivedPushImmutableTransform<
          TTargetParams,
          TTargetData,
          TSourceParams,
          TSourceData
        >
      | undefined;
    const drafted = !!pushTransform;
    if (pushFn) {
      linkBuilder.pushState(({ dataChanged, self, other }) => {
        if (!dataChanged || self.state?.data === undefined || !other.state) {
          return;
        }
        const selfData = self.state.data;
        const run = (sourceData?: TSourceData) =>
          pushFn({
            self: { params: self.params, data: selfData },
            other: { params: other.params, data: sourceData },
          });
        const data = drafted
          ? produceNext(other.state.data, (draft) =>
              run(draft as TSourceData | undefined)
            )
          : run(other.state.data);
        if (data !== undefined) {
          return applyState(other.state, toSuccess(data)());
        }
      });
    }

    const links: Link<any, any, any, any>[] = [
      linkBuilder
        .pullBehavior({ state: true, invalidation: true })
        .pushBehavior({ state: !!pushFn, invalidation: true })
        .finalize(),
    ];

    for (const deferred of deferredLinks) {
      const resolvedTarget =
        deferred.target === SELF ? resource : deferred.target;

      if (!resolvedTarget) {
        throw new Error(
          'Target is undefined. Please check for an import cycle'
        );
      }

      links.push(
        deferred.construct(new LinkBuilder(resource, resolvedTarget)).finalize()
      );
    }

    __registry.registerResource(resource, links);

    return resource as Resource<TTargetParams, TTargetData, TSourceError>;
  };

  const linksResult: LinkResult<TTargetParams, TTargetData, TSourceError> = {
    register,
    linkTo: (
      linkTgt: AnyResourceDefinition | SelfLink,
      construct: (builder: LinkBuilder<any, any, any, any>) => {
        finalize(): Link<any, any, any, any>;
      }
    ) => {
      deferredLinks.push({
        target: linkTgt as any,
        construct: construct as any,
      });
      return linksResult;
    },
  };

  const builder: Builder<
    TSourceParams,
    TSourceData,
    TTargetData,
    TTargetParams,
    TSourceError
  > = {
    push: (fn) => {
      pushTransform = fn;
      return builder;
    },
    pushImmutable: (fn) => {
      pushImmutableTransform = fn;
      return builder;
    },
    withHash: (fn: typeof hash) => {
      hash = fn;
      return builder;
    },

    ...linksResult,
  };
  return builder;
};

export const deriveResource = (name: string) => ({
  from: <TSourceParams, TSourceData, TSourceError extends Error = Error>(
    source: ResourceDefinition<TSourceParams, TSourceData, TSourceError>
  ) => {
    const buildPullStep = <TTargetParams>(
      paramMapper: (params: TTargetParams) => TSourceParams,
      args: Array<ArgEntry<TTargetParams>> | undefined,
      mergeWithSource: boolean
    ): PullStep<TTargetParams, TSourceParams, TSourceData, TSourceError> =>
      ({
        pull: <TTargetData>(
          transform: DerivedPullTransform<
            TTargetParams,
            TTargetData,
            TSourceParams,
            TSourceData
          >
        ) =>
          buildDeriveChain<
            TSourceParams,
            TSourceData,
            TTargetParams,
            TTargetData,
            TSourceError
          >(name, source, paramMapper, transform, args, mergeWithSource),
        /**
         * Append these args to the source's args. Use when the derived resource
         * adds refinement keys on top of the source's identity (the common case).
         */
        mergeArgs: (keys: Array<ArgEntry<TTargetParams>>) =>
          buildPullStep(paramMapper, keys, true),
        /**
         * Use exactly these args, ignoring the source's args. Use when the
         * derived resource intentionally narrows identity (rare).
         */
        withArgs: (keys: Array<ArgEntry<TTargetParams>>) =>
          buildPullStep(paramMapper, keys, false),
      }) as PullStep<TTargetParams, TSourceParams, TSourceData, TSourceError>;

    // After `.mapParams` the derived has its own param shape, so identity must
    // be declared explicitly — no `.pull` until `.mergeArgs`/`.withArgs`.
    const buildMappedStep = <TTargetParams>(
      paramMapper: (params: TTargetParams) => TSourceParams
    ): MappedStep<TTargetParams, TSourceParams, TSourceData, TSourceError> => ({
      mergeArgs: (keys: Array<ArgEntry<TTargetParams>>) =>
        buildPullStep(paramMapper, keys, true),
      withArgs: (keys: Array<ArgEntry<TTargetParams>>) =>
        buildPullStep(paramMapper, keys, false),
    });

    return {
      pull: buildPullStep<TSourceParams>((p) => p, undefined, true).pull,
      mapParams: <TTargetParams>(
        paramMapper: (params: TTargetParams) => TSourceParams
      ) => buildMappedStep(paramMapper),
    };
  },
});
